/**
 * Experiments admin hooks (#25). Mirrors
 * `api/src/experiments/experiments.routes.ts`:
 *
 *   - GET    /api/admin/sites/:siteId/experiments       — paginated list
 *   - POST   /api/admin/sites/:siteId/experiments       — create
 *   - GET    /api/admin/experiments/:id                 — detail w/ variants
 *   - PATCH  /api/admin/experiments/:id                 — update (DRAFT only)
 *   - POST   /api/admin/experiments/:id/start           — DRAFT → RUNNING
 *   - POST   /api/admin/experiments/:id/stop            — RUNNING → COMPLETED
 *   - POST   /api/admin/experiments/:id/variants        — add variant
 *   - PATCH  /api/admin/variants/:id                    — update variant
 *   - DELETE /api/admin/variants/:id                    — delete variant
 *
 * Mutations invalidate `queryKeys.experiments.all` so both the list view
 * and the currently-open detail page re-fetch immediately after a save.
 * The detail key is also explicitly invalidated by id for the same
 * "no flash of stale data" reason as the leads slice.
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type {
  CreateExperimentInput,
  CreateVariantInput,
  ExperimentDetailResponse,
  ExperimentMetricsResponse,
  ExperimentResponse,
  ExperimentStatus,
  ExperimentVariantResponse,
  UpdateExperimentInput,
  UpdateVariantInput,
} from "@ottopilot/shared";
import { apiDelete, apiGet, apiList, apiPatch, apiPost } from "../lib/api";
import type { ApiClientError, PageMeta } from "../lib/api";
import { queryKeys } from "./queryKeys";

export type ExperimentsListParams = {
  siteId: string;
  page: number;
  pageSize: number;
  status?: ExperimentStatus;
  /**
   * Issue #253: when true, the list includes ARCHIVED rows (the default
   * is to hide them). When the caller passes an explicit `status`
   * filter, this is ignored server-side (the explicit filter wins).
   */
  includeArchived?: boolean;
};

export type ExperimentsListResult = {
  data: ExperimentResponse[];
  meta: PageMeta | undefined;
};

function buildExperimentsQuery(params: ExperimentsListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: params.page,
    pageSize: params.pageSize,
  };
  if (params.status) out.status = params.status;
  // Issue #253: only emit `includeArchived=true` when truthy — keep the
  // default request URL clean (no flag) so the cache key matches the
  // operator's "hide archived" mental model.
  if (params.includeArchived) out.includeArchived = "true";
  return out;
}

export function useExperiments(params: ExperimentsListParams) {
  return useQuery<ExperimentsListResult, ApiClientError>({
    queryKey: queryKeys.experiments.listForSite(params.siteId, {
      page: params.page,
      pageSize: params.pageSize,
      status: params.status,
      includeArchived: params.includeArchived ?? false,
    }),
    queryFn: () =>
      apiList<ExperimentResponse[]>(`/api/admin/sites/${params.siteId}/experiments`, {
        query: buildExperimentsQuery(params),
      }),
    placeholderData: keepPreviousData,
    enabled: Boolean(params.siteId),
  });
}

export function useExperimentDetail(id: string | undefined) {
  return useQuery<ExperimentDetailResponse, ApiClientError>({
    queryKey: queryKeys.experiments.detail(id ?? ""),
    queryFn: () => apiGet<ExperimentDetailResponse>(`/api/admin/experiments/${id ?? ""}`),
    enabled: Boolean(id),
  });
}

export function useCreateExperiment(siteId: string) {
  const qc = useQueryClient();
  return useMutation<ExperimentResponse, ApiClientError, CreateExperimentInput>({
    mutationFn: (input) =>
      apiPost<ExperimentResponse>(`/api/admin/sites/${siteId}/experiments`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.all });
    },
  });
}

export function useUpdateExperiment(id: string) {
  const qc = useQueryClient();
  return useMutation<ExperimentResponse, ApiClientError, UpdateExperimentInput>({
    mutationFn: (input) => apiPatch<ExperimentResponse>(`/api/admin/experiments/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.all });
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.detail(id) });
    },
  });
}

export function useStartExperiment(id: string) {
  const qc = useQueryClient();
  return useMutation<ExperimentResponse, ApiClientError, void>({
    mutationFn: () => apiPost<ExperimentResponse>(`/api/admin/experiments/${id}/start`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.all });
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.detail(id) });
    },
  });
}

export function useStopExperiment(id: string) {
  const qc = useQueryClient();
  return useMutation<ExperimentResponse, ApiClientError, void>({
    mutationFn: () => apiPost<ExperimentResponse>(`/api/admin/experiments/${id}/stop`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.all });
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.detail(id) });
    },
  });
}

/**
 * Issue #253 — COMPLETED → RUNNING. Same envelope as start/stop; the
 * server re-runs the start-time validation (variant target eligibility
 * + lockguard reapply) so the SPA surfaces precise error codes
 * (`VARIANT_TARGET_NOT_TRAFFIC_READY`, `WEIGHTS_DO_NOT_SUM_TO_100`,
 * etc.) when the operator tries to resume an experiment whose state
 * the world drifted away from while it was COMPLETED.
 */
export function useResumeExperiment(id: string) {
  const qc = useQueryClient();
  return useMutation<ExperimentResponse, ApiClientError, void>({
    mutationFn: () => apiPost<ExperimentResponse>(`/api/admin/experiments/${id}/resume`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.all });
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.detail(id) });
    },
  });
}

/**
 * Issue #253 — DRAFT|COMPLETED → ARCHIVED. After success the SPA
 * navigates the operator back to the list (the detail page would render
 * mostly-disabled buttons on an ARCHIVED row); the hook itself only
 * issues the POST + invalidates caches.
 */
export function useArchiveExperiment(id: string) {
  const qc = useQueryClient();
  return useMutation<ExperimentResponse, ApiClientError, void>({
    mutationFn: () => apiPost<ExperimentResponse>(`/api/admin/experiments/${id}/archive`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.all });
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.detail(id) });
    },
  });
}

export function useCreateVariant(experimentId: string) {
  const qc = useQueryClient();
  return useMutation<ExperimentVariantResponse, ApiClientError, CreateVariantInput>({
    mutationFn: (input) =>
      apiPost<ExperimentVariantResponse>(`/api/admin/experiments/${experimentId}/variants`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.detail(experimentId) });
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.all });
    },
  });
}

export function useUpdateVariant(experimentId: string) {
  const qc = useQueryClient();
  return useMutation<
    ExperimentVariantResponse,
    ApiClientError,
    { variantId: string; input: UpdateVariantInput }
  >({
    mutationFn: ({ variantId, input }) =>
      apiPatch<ExperimentVariantResponse>(`/api/admin/variants/${variantId}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.detail(experimentId) });
    },
  });
}

export function useDeleteVariant(experimentId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (variantId) => apiDelete<void>(`/api/admin/variants/${variantId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.detail(experimentId) });
    },
  });
}

/**
 * Fetch the experiment metrics dashboard payload (#27). Disabled when
 * the caller passes `undefined` so the hook is safe to call on the
 * detail page during the initial loading state. Refetched manually by
 * mutations is unnecessary — the API caches results 60s in-process,
 * and the dashboard's natural usage doesn't change those numbers.
 */
export function useExperimentMetrics(id: string | undefined, enabled = true) {
  return useQuery<ExperimentMetricsResponse, ApiClientError>({
    queryKey: queryKeys.experiments.metrics(id ?? ""),
    queryFn: () => apiGet<ExperimentMetricsResponse>(`/api/admin/experiments/${id ?? ""}/metrics`),
    enabled: Boolean(id) && enabled,
  });
}
